// Adverbs — how to form them (-mente), where they go, and the high-frequency
// ones worth memorising whole. A grouped reference table sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = {
  Time: [
    ["adesso / ora", "now"], ["oggi", "today"], ["ieri", "yesterday"], ["domani", "tomorrow"],
    ["sempre", "always"], ["mai", "never"], ["già", "already"], ["ancora", "still / yet"],
    ["presto", "early / soon"], ["tardi", "late"], ["poi", "then / later"], ["subito", "right away"],
  ],
  Place: [
    ["qui / qua", "here"], ["lì / là", "there"], ["vicino", "near"], ["lontano", "far"],
    ["dentro", "inside"], ["fuori", "outside"], ["sopra", "above"], ["sotto", "below"],
    ["davanti", "in front"], ["dietro", "behind"], ["intorno", "around"], ["dappertutto", "everywhere"],
  ],
  Manner: [
    ["bene", "well"], ["male", "badly"], ["così", "like this / so"], ["piano", "slowly / softly"],
    ["forte", "loudly / hard"], ["insieme", "together"], ["volentieri", "gladly"], ["meglio", "better"],
  ],
  Quantity: [
    ["molto", "very / a lot"], ["poco", "little"], ["abbastanza", "quite / enough"], ["troppo", "too much"],
    ["più", "more"], ["meno", "less"], ["quasi", "almost"], ["tanto", "so much"],
  ],
};

export function adverbsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${block("Time", COMMON.Time)}
      ${block("Place", COMMON.Place)}
      ${block("Manner", COMMON.Manner)}
      ${block("Quantity", COMMON.Quantity)}
    </div>`;

  renderGuideSections(
    app,
    "Adverbs",
    "Adverbs describe how, when, where, or to what degree something happens. Unlike adjectives, they never change form to agree with anything — one shape fits all.",
    [
      {
        title: "1. Forming adverbs with -mente",
        body: `
          <p class="text-sage-700 mb-3">Take the <b>feminine singular</b> of an adjective and add <b>-mente</b> (the equivalent of English <i>-ly</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-3">
            <li><i>rapida → <b>rapidamente</b></i> (quickly)</li>
            <li><i>lenta → <b>lentamente</b></i> (slowly)</li>
            <li><i>vera → <b>veramente</b></i> (truly)</li>
          </ul>
          <p class="text-sage-700 text-sm"><b>Adjectives ending in -le or -re drop the final -e</b> before -mente: <i>facile → <b>facilmente</b></i> (easily), <i>regolare → <b>regolarmente</b></i> (regularly). And some everyday adverbs aren't built this way at all: <i>bene, male, volentieri</i>.</p>
        `,
      },
      {
        title: "2. Position",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Adverbs of <b>manner</b> usually follow the verb: <i>Guida <b>piano</b>.</i></li>
            <li>Adverbs modifying an <b>adjective/adverb</b> go before it: <i><b>molto</b> alto, <b>abbastanza</b> bene</i>.</li>
            <li>Unlike Spanish (muy/mucho), Italian uses <i><b>molto</b></i> for both: <i>molto alto</i> (very tall) and <i>mangia molto</i> (eats a lot) — it only agrees when it's an adjective (<i>molti libri</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Adverbial phrases (no -mente needed)",
        body: `
          <p class="text-sage-700 mb-2">Italian often prefers a short phrase over a long -mente word:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>con attenzione</i> = carefully · <i>di solito</i> = usually</li>
            <li><i>all'improvviso</i> = suddenly · <i>per fortuna</i> = luckily · <i>spesso</i> = often</li>
            <li><i>di nuovo</i> = again · <i>in fretta</i> = in a hurry · <i>a poco a poco</i> = little by little</li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
